import { useState, useEffect } from 'react';
import { Search, Plus, Copy, CheckCircle, Package, Zap, BookOpen, ChevronDown, ChevronRight, Tag, Shield, Bot, X, Upload, Loader2, AlertTriangle, Trash2, Download, FileCode, Pencil, Power } from 'lucide-react';
import PageAiGuide from './PageAiGuide';
import FieldGuide from './FieldGuide';

const CAP_HINTS = [
  { kind: 'explain', field: '封装形态', text: 'API 原子能力（经网关代理）/ MCP Tool（进本实例 MCP Server）/ Skill 知识包（ZIP 离线）。供超级智能体调用的需提供 FC Schema。' },
  { kind: 'fact', field: '真实后端地址 / 后端凭证', text: '事实型，需按本现场实际填写，AI 不代填；凭证由本实例网关加密保管、调用时代填。' },
  { kind: 'explain', field: '请求参数录入', text: '逐行填参数名/类型/必填/说明/示例值即可，系统自动生成 Schema（兼 FC Schema），无需手写 JSON；也可粘贴 cURL/OpenAPI 导入。' },
  { kind: 'explain', field: '后端认证方式', text: '指网关调真实后端的认证（无/API Key/Bearer/Basic/自定义）；调用方→网关固定用平台 IAM Token，无需在此配。' },
];

// 状态机（产品团队统一注册，测试通过即提交，无"待验证/待审核"）：可用 / 异常 / 已停用
const STATUS_META = {
  available: { label: '可用', dot: 'bg-green-500', text: 'text-green-600', desc: '接口测试通过 + 轮询正常' },
  abnormal: { label: '异常', dot: 'bg-red-500', text: 'text-red-500', desc: '轮询检测到端点不可达/认证失败/超时' },
  disabled: { label: '已停用', dot: 'bg-gray-400', text: 'text-gray-400', desc: '管理员手动下线' },
};

const CAPABILITIES = [
  {
    id: 1, type: 'api', category: '告警管理', name: '当前告警批量查询',
    description: '根据网元ID、告警级别、时间范围批量拉取当前活跃告警列表，支持分页。',
    proxyEndpoint: 'POST https://echo-gw.internal/proxy/alarm/current/list',
    realBackend: 'http://nms-alarm-svc.internal:8080/...（网络隔离）',
    fcSchema: true, superAgentCallable: true, status: 'available',
    params: [
      { name: 'neId', type: 'string', required: true, desc: '网元ID', sample: 'NE-HB-Core-01' },
      { name: 'alarmLevel', type: 'string', options: 'critical,major,minor', required: false, desc: '告警级别', sample: 'critical' },
      { name: 'pageSize', type: 'integer', required: false, desc: '每页条数', sample: '20' },
    ],
    responseExample: '{ "total": 120, "list": [{ "alarmId": "ALM-001", "level": "critical" }] }',
    version: 'v2.1', callCount: 3421,
  },
  {
    id: 2, type: 'api', category: 'KPI分析', name: 'KPI指标时序查询',
    description: '查询指定网元在时间范围内的KPI时序数据，支持多指标聚合。',
    proxyEndpoint: 'POST https://echo-gw.internal/proxy/kpi/timeseries',
    realBackend: 'http://nms-kpi-svc.internal:9090/...（网络隔离）',
    fcSchema: true, superAgentCallable: true, status: 'available',
    params: [
      { name: 'neId', type: 'string', required: true, desc: '网元ID', sample: 'NE-SH-Edge-07' },
      { name: 'metrics', type: 'array', items: 'string', required: true, desc: '指标列表', sample: '["cpu","mem"]' },
      { name: 'timeRange', type: 'object', required: true, desc: '时间范围（对象）', sample: '', fields: [
        { name: 'start', type: 'string', required: true, desc: '开始时间(ISO8601)' },
        { name: 'end', type: 'string', required: true, desc: '结束时间(ISO8601)' },
      ] },
    ],
    responseExample: '{ "data": [{ "time": "2026-06-01T00:00:00Z", "cpu": 45.2 }] }',
    version: 'v3.0', callCount: 2143,
  },
  {
    id: 3, type: 'mcp', category: '告警管理', name: 'alarm_query_tool',
    description: '告警查询能力的MCP工具封装，本实例 Dify/Coze 配置本实例 MCP Server 地址后自动发现。',
    toolName: 'alarm_query_tool',
    fcSchema: true, superAgentCallable: true, status: 'abnormal',
    params: [
      { name: 'query_type', type: 'string', options: 'current,history', required: true, desc: '查询类型', sample: 'current' },
      { name: 'ne_id', type: 'string', required: false, desc: '网元ID', sample: 'NE-HB-Core-01' },
    ],
    responseExample: '{ "alarms": [...], "count": 42 }',
    version: 'v1.0', callCount: 1532, errMsg: 'Connect Timeout（最后探测 10:22）',
  },
  {
    id: 4, type: 'api', category: '配置查询', name: '设备配置快照查询',
    description: '获取指定网元的最新配置快照，支持按模块过滤（接口/路由/ACL等）。',
    proxyEndpoint: 'GET https://echo-gw.internal/proxy/config/snapshot/{neId}',
    realBackend: 'http://nms-config-svc.internal:8081/...（网络隔离）',
    fcSchema: true, superAgentCallable: true, status: 'disabled',
    params: [
      { name: 'neId', type: 'string', required: true, desc: '网元ID', sample: 'NE-GZ-Access-12' },
      { name: 'module', type: 'string', required: false, desc: '配置模块', sample: 'interfaces' },
    ],
    responseExample: '{ "neId": "NE-001", "config": { "interfaces": [...] } }',
    version: 'v1.0', callCount: 987,
  },
  {
    id: 5, type: 'skill', category: '故障处理', name: 'IP承载网故障诊断指南',
    description: 'IP承载网主流故障场景的诊断提示词模板、历史案例知识文档、标准处理SOP。',
    files: ['fault_diagnosis_prompt.md', 'ip_network_cases.pdf', 'sop_handbook.docx'],
    fcSchema: false, superAgentCallable: false, status: 'available',
    version: 'v1.2', downloadCount: 56,
  },
];

const TYPE_META = {
  api: { label: 'API原子能力', icon: Zap, color: 'blue', desc: '经平台网关代理的RESTful API' },
  mcp: { label: 'MCP Tool', icon: Package, color: 'purple', desc: '配置本实例MCP Server地址自动发现' },
  skill: { label: 'Skill知识包', icon: BookOpen, color: 'green', desc: 'ZIP知识包，离线融入System Prompt' },
};
const CATEGORIES = ['全部', '告警管理', 'KPI分析', '配置查询', '故障处理'];

function CopyBtn({ text, label = '复制' }) {
  const [c, setC] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard?.writeText(text); setC(true); setTimeout(() => setC(false), 2000); }}
      className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded">
      {c ? <CheckCircle className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}{c ? '已复制' : label}
    </button>
  );
}

// 详情卡片：递归只读渲染参数树（任意层 object 子字段缩进展示）
function renderParamRowsRO(nodes, depth) {
  return (nodes || []).map((pp, i) => (
    <div key={i} className="border-t border-gray-100">
      <div className="grid grid-cols-[1.2fr_0.8fr_0.5fr_1.5fr_1.2fr] gap-2 px-2 py-1.5 text-xs text-gray-600" style={depth > 0 ? { marginLeft: depth * 12 } : undefined}>
        <span className="font-mono text-blue-600 truncate">{depth > 0 ? '└ ' : ''}{pp.name}</span>
        <span className="text-gray-400">{pp.type === 'array' ? `array<${pp.items || 'string'}>` : pp.type}{pp.options ? ' · enum' : ''}</span>
        <span>{pp.required ? <span className="text-red-500">是</span> : '否'}</span>
        <span>{pp.desc}{pp.options ? <span className="text-gray-400"> · 可选值 {pp.options}</span> : ''}</span>
        <span className="font-mono text-gray-400 truncate">{pp.type === 'object' ? <span className="text-gray-300 italic">由子字段构成</span> : pp.sample}</span>
      </div>
      {pp.type === 'object' && (pp.fields || []).length > 0 && renderParamRowsRO(pp.fields, depth + 1)}
    </div>
  ));
}

// ───────── 注册抽屉：参数表格录入 + 导入 + 测试连接（无手写 JSON、无待验证）─────────
// 参数为递归树：每个节点 { name,type,items,options,required,desc,sample,fields[] }，object 节点用 fields 承载子字段（任意层）
const emptyParam = () => ({ name: '', type: 'string', items: 'string', options: '', required: false, desc: '', sample: '', fields: [] });
// 在树上按路径(path=索引数组)更新/增删节点
const updateAt = (nodes, path, fn) => { const [i, ...rest] = path; return nodes.map((n, idx) => idx !== i ? n : (rest.length === 0 ? fn(n) : { ...n, fields: updateAt(n.fields || [], rest, fn) })); };
const removeAt = (nodes, path) => { const [i, ...rest] = path; if (rest.length === 0) return nodes.filter((_, idx) => idx !== i); return nodes.map((n, idx) => idx !== i ? n : { ...n, fields: removeAt(n.fields || [], rest) }); };

function RegisterDrawer({ open, editCap, onClose, onDone }) {
  const [form, setForm] = useState({ type: 'api', name: '', category: '告警管理', description: '', method: 'POST', realBackend: '', auth: 'apikey', superAgentCallable: true });
  const [params, setParams] = useState([emptyParam()]);
  const [collapsed, setCollapsed] = useState({}); // object 子树折叠状态：pathKey -> true
  const [testState, setTestState] = useState('idle'); // idle | testing | ok | fail
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); if (['realBackend', 'method', 'auth', 'keyName', 'secret', 'user', 'pass'].includes(k)) setTestState('idle'); };

  // 打开时：编辑则预填，新建则重置
  useEffect(() => {
    if (!open) return;
    if (editCap) {
      setForm({ type: editCap.type, name: editCap.name, category: editCap.category, description: editCap.description || '', method: editCap.method || 'POST', realBackend: editCap.realBackend || '', auth: editCap.auth || 'apikey', superAgentCallable: !!editCap.superAgentCallable });
      setParams(editCap.params && editCap.params.length ? editCap.params.map(p => ({ ...p })) : [emptyParam()]);
      setTestState(editCap.type === 'skill' || editCap.status === 'available' ? 'ok' : 'idle'); // 已上架视为已测通过
    } else {
      setForm({ type: 'api', name: '', category: '告警管理', description: '', method: 'POST', realBackend: '', auth: 'apikey', superAgentCallable: true });
      setParams([emptyParam()]);
      setTestState('idle');
    }
  }, [open, editCap]);

  if (!open) return null;

  // 递归树：按路径增删改（path=索引数组）
  const setNode = (path, k, v) => { setParams(prev => updateAt(prev, path, n => ({ ...n, [k]: v }))); setTestState('idle'); };
  const addRoot = () => setParams(prev => [...prev, emptyParam()]);
  const addChild = (path) => { setParams(prev => updateAt(prev, path, n => ({ ...n, fields: [...(n.fields || []), emptyParam()] }))); setCollapsed(c => ({ ...c, [path.join('.')]: false })); setTestState('idle'); };
  const delNode = (path) => { setParams(prev => removeAt(prev, path)); setTestState('idle'); };
  const toggleCollapse = (path) => setCollapsed(c => ({ ...c, [path.join('.')]: !c[path.join('.')] }));

  // 导入：从 cURL / OpenAPI 解析参数（演示：填充一组示例参数）
  const doImport = () => {
    setParams([
      { name: 'neId', type: 'string', items: 'string', options: '', required: true, desc: '网元ID', sample: 'NE-HB-Core-01' },
      { name: 'level', type: 'string', items: 'string', options: 'critical,major,minor', required: false, desc: '告警级别', sample: 'critical' },
      { name: 'limit', type: 'integer', items: 'string', options: '', required: false, desc: '返回条数', sample: '20' },
    ]);
    if (!form.realBackend) set('realBackend', 'http://nms-alarm-svc.internal:8080/alarm/current/list');
    setShowImport(false); setImportText('');
  };

  const runTest = () => { setTestState('testing'); setTimeout(() => setTestState(/fail|err|x{3}/i.test(form.realBackend) ? 'fail' : 'ok'), 1100); };
  const tested = testState === 'ok';
  const validParams = params.filter(p => p.name.trim());
  const canSubmit = form.name && form.description && (form.type === 'skill' || (tested && validParams.length > 0));

  // 递归渲染参数树：任意层 object 子字段；缩进+折叠，深层提示用导入
  const PH = { string: 'NE-HB-Core-01', integer: '20', number: '0.6', boolean: 'true / false', array: '["cpu","mem"]', object: '{ ... }' };
  const renderNodes = (nodes, parentPath, depth) => nodes.map((p, i) => {
    const path = [...parentPath, i];
    const isObj = p.type === 'object';
    const folded = collapsed[path.join('.')];
    const isRoot = depth === 0;
    return (
      <div key={i} className="border-t border-gray-100" style={depth > 0 ? { marginLeft: 12, paddingLeft: 10, borderLeft: '2px solid #E0E7FF' } : undefined}>
        <div className="grid grid-cols-[1.3fr_0.9fr_40px_1.3fr_1.3fr_28px] gap-2 px-2 py-1.5 items-center">
          <div className="flex items-center gap-1 min-w-0">
            {isObj
              ? <button onClick={() => toggleCollapse(path)} className="text-gray-400 hover:text-gray-600 flex-shrink-0" title={folded ? '展开子字段' : '折叠子字段'}>{folded ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}</button>
              : <span className="w-3.5 flex-shrink-0" />}
            <input value={p.name} onChange={e => setNode(path, 'name', e.target.value)} placeholder={isRoot ? 'neId' : 'start'} className="px-2 py-1 text-xs border border-gray-200 rounded font-mono min-w-0 flex-1" />
          </div>
          <select value={p.type} onChange={e => setNode(path, 'type', e.target.value)} className="px-1 py-1 text-xs border border-gray-200 rounded min-w-0"><option>string</option><option>integer</option><option>number</option><option>boolean</option><option>array</option><option>object</option></select>
          <input type="checkbox" checked={p.required} onChange={e => setNode(path, 'required', e.target.checked)} className="accent-blue-600 mx-auto" />
          <input value={p.desc} onChange={e => setNode(path, 'desc', e.target.value)} placeholder="说明" className="px-2 py-1 text-xs border border-gray-200 rounded min-w-0" />
          {isObj
            ? <span className="px-2 py-1 text-[11px] text-gray-300 italic min-w-0 truncate" title="object 的值由子字段构成，示例值在叶子字段上填">由子字段构成</span>
            : <input value={p.sample} onChange={e => setNode(path, 'sample', e.target.value)} placeholder={PH[p.type] || ''} className="px-2 py-1 text-xs border border-gray-200 rounded font-mono min-w-0" />}
          <button onClick={() => delNode(path)} disabled={isRoot && nodes.length === 1} title={isRoot && nodes.length === 1 ? '至少保留一个参数' : '删除'} className="p-1 text-gray-300 hover:text-red-500 disabled:opacity-30 disabled:hover:text-gray-300"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
        {p.type === 'array' && (
          <div className="px-2 pb-1.5 flex items-center gap-2">
            <span className="text-[11px] text-gray-400 whitespace-nowrap pl-1">数组元素类型</span>
            <select value={p.items || 'string'} onChange={e => setNode(path, 'items', e.target.value)} className="px-2 py-1 text-xs border border-gray-200 rounded"><option>string</option><option>integer</option><option>number</option><option>boolean</option></select>
          </div>
        )}
        {(p.type === 'string' || p.type === 'integer') && (
          <div className="px-2 pb-1.5 flex items-center gap-2">
            <span className="text-[11px] text-gray-400 whitespace-nowrap pl-1">可选值(enum)</span>
            <input value={p.options || ''} onChange={e => setNode(path, 'options', e.target.value)} placeholder="留空=不限定；逗号分隔，如 critical,major,minor" className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded font-mono min-w-0" />
          </div>
        )}
        {isObj && !folded && (
          <div className="pb-2">
            {depth >= 2 && <div className="px-2 py-1 ml-3 text-[11px] text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3 flex-shrink-0" /> 嵌套已较深，建议改用「从 cURL/OpenAPI 导入」自动解析</div>}
            {(p.fields || []).length === 0 && <div className="px-2 ml-3 text-[11px] text-gray-300 py-1">暂无子字段</div>}
            {renderNodes(p.fields || [], path, depth + 1)}
            <button onClick={() => addChild(path)} className="ml-5 mt-1 text-[11px] text-indigo-500 hover:text-indigo-700 flex items-center gap-0.5"><Plus className="w-3 h-3" /> 添加子字段</button>
          </div>
        )}
      </div>
    );
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-[640px] bg-white h-full shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">{editCap ? '编辑能力' : '注册新能力'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded text-gray-400"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">封装形态 <span className="text-red-500">*</span></label>
            <div className="flex gap-2">
              {[['api', 'API原子能力'], ['mcp', 'MCP Tool'], ['skill', 'Skill知识包']].map(([k, l]) => (
                <button key={k} onClick={() => set('type', k)} className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg border ${form.type === k ? 'bg-blue-50 text-blue-700 border-blue-300' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">能力名称 <span className="text-red-500">*</span></label>
              <FieldGuide label="能力名称" value={form.name} meta={{ nature: 'info', explain: form.type === 'mcp' ? 'MCP Tool 名建议用英文小写下划线（如 alarm_query_tool），将作为工具标识被 Dify/Coze 自动发现。' : '能力的业务名称，超级智能体路由与 Agent 开发者据此识别用途，建议含核心实体与操作（如「当前告警批量查询」）。' }}>
                <input value={form.name} onChange={e => set('name', e.target.value)} placeholder={form.type === 'mcp' ? 'alarm_query_tool' : '当前告警批量查询'} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </FieldGuide>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">业务分类 <span className="text-red-500">*</span></label>
              <select value={form.category} onChange={e => set('category', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg">
                {['告警管理', 'KPI分析', '配置查询', '故障处理'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">能力描述 <span className="text-red-500">*</span></label>
            <FieldGuide label="能力描述" value={form.description} meta={{ nature: 'info', explain: '这是超级智能体路由判断的关键依据：描述越准确（含核心实体 + 操作意图 + 适用场景），被正确调用的概率越高。建议一句话讲清「能做什么、查/改什么对象」。' }}>
              <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} placeholder="供 Agent 开发者与超级智能体理解用途，建议含核心实体与操作意图" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </FieldGuide>
          </div>

          {form.type === 'skill' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">上传 Skill ZIP 包 <span className="text-red-500">*</span></label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-400 hover:border-blue-400 cursor-pointer">
                <Upload className="w-6 h-6 mx-auto mb-2" /><p className="text-xs">点击或拖拽上传（知识文档+提示词模板+资产文件）</p>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">真实后端地址 <span className="text-red-500">*</span></label>
                <div className="flex gap-2">
                  <select value={form.method} onChange={e => set('method', e.target.value)} className="w-20 px-2 py-2 text-sm border border-gray-200 rounded-lg"><option>POST</option><option>GET</option></select>
                  <div className="flex-1">
                    <FieldGuide label="真实后端地址" value={form.realBackend} meta={{ nature: 'fact', explain: '本现场真实可达的后端服务地址，事实型字段，需按本项目实际部署填写，AI 无法代填。对调用方隐藏，测试通过后系统自动生成对外代理端点。' }}>
                      <input value={form.realBackend} onChange={e => set('realBackend', e.target.value)} placeholder="http://nms-alarm-svc.internal:8080/..." className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </FieldGuide>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><Shield className="w-3 h-3" /> 对调用方隐藏，测试通过后系统生成代理端点</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">后端认证方式</label>
                <select value={form.auth} onChange={e => set('auth', e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg">
                  <option value="none">无需认证（内网直连）</option>
                  <option value="apikey">API Key（Header / Query）</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="basic">Basic Auth（账号 / 密码）</option>
                  <option value="custom">自定义 Header</option>
                </select>
                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1"><Shield className="w-3 h-3" /> 网关代理调用真实后端时使用；凭证在此填一次、由本实例网关加密保管，调用时网关自动代填（调用方→网关固定用平台 IAM Token，无需在此配）</p>

                {/* 凭证联动输入：选了认证方式就在这里把凭证填了（注册的一部分，不另起步骤） */}
                {form.auth === 'apikey' && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <input value={form.keyName || ''} onChange={e => set('keyName', e.target.value)} placeholder="参数名（如 X-API-Key）" className="px-3 py-2 text-sm border border-gray-200 rounded-lg font-mono" />
                    <input type="password" value={form.secret || ''} onChange={e => set('secret', e.target.value)} placeholder="密钥值" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                  </div>
                )}
                {form.auth === 'bearer' && (
                  <input type="password" value={form.secret || ''} onChange={e => set('secret', e.target.value)} placeholder="Bearer Token" className="mt-2 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                )}
                {form.auth === 'basic' && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <input value={form.user || ''} onChange={e => set('user', e.target.value)} placeholder="用户名" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                    <input type="password" value={form.pass || ''} onChange={e => set('pass', e.target.value)} placeholder="密码" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                  </div>
                )}
                {form.auth === 'custom' && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <input value={form.keyName || ''} onChange={e => set('keyName', e.target.value)} placeholder="Header 名" className="px-3 py-2 text-sm border border-gray-200 rounded-lg font-mono" />
                    <input type="password" value={form.secret || ''} onChange={e => set('secret', e.target.value)} placeholder="Header 值" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                  </div>
                )}
              </div>

              {/* 参数：可视化表格录入 + 导入（替代手写 JSON） */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-gray-700">请求参数 <span className="text-red-500">*</span>
                    <span className="text-xs text-gray-400 font-normal ml-1">逐行填写，系统据此生成 Schema（兼 FC Schema），无需手写 JSON</span>
                  </label>
                  <button onClick={() => setShowImport(!showImport)} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><FileCode className="w-3 h-3" /> 从 cURL / OpenAPI 导入</button>
                </div>

                {showImport && (
                  <div className="mb-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <textarea value={importText} onChange={e => setImportText(e.target.value)} rows={3} placeholder={'粘贴 cURL 命令或 OpenAPI Schema，自动解析为参数表格…\ncurl -X POST .../alarm/current/list -d \'{"neId":"NE-..."}\''} className="w-full px-2 py-1.5 text-xs font-mono border border-gray-200 rounded resize-none" />
                    <div className="flex justify-end gap-2 mt-2">
                      <button onClick={() => setShowImport(false)} className="text-xs px-2 py-1 text-gray-500">取消</button>
                      <button onClick={doImport} className="text-xs px-3 py-1 bg-blue-600 text-white rounded">解析并填充</button>
                    </div>
                  </div>
                )}

                {/* 参数树（类型对齐 JSON Schema：array 选元素类型；string/integer 可填可选值=enum；object 递归任意层子字段） */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="grid grid-cols-[1.3fr_0.9fr_40px_1.3fr_1.3fr_28px] gap-2 px-2 py-1.5 bg-gray-50 text-xs text-gray-500 font-medium items-center">
                    <span>参数名</span><span>类型</span><span className="text-center">必填</span><span>说明</span><span>示例值</span><span></span>
                  </div>
                  {renderNodes(params, [], 0)}
                  <button onClick={addRoot} className="w-full py-1.5 text-xs text-blue-600 hover:bg-blue-50 border-t border-gray-100 flex items-center justify-center gap-1"><Plus className="w-3 h-3" /> 添加参数</button>
                </div>
              </div>

              {/* 测试连接（用示例值一键组装请求）—— 提交前置 */}
              <div>
                <button onClick={runTest} disabled={!form.realBackend || validParams.length === 0 || testState === 'testing'}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg ${(!form.realBackend || validParams.length === 0) ? 'bg-gray-100 text-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                  {testState === 'testing' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                  {testState === 'testing' ? '测试中…' : '测试连接（用示例值组装请求）'}
                </button>
                {tested && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 border border-green-200 rounded-lg px-2.5 py-1.5"><CheckCircle className="w-3.5 h-3.5" /> 测试通过（HTTP 200，0.8s）</div>
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input type="checkbox" checked={form.superAgentCallable} onChange={e => set('superAgentCallable', e.target.checked)} className="accent-indigo-600" />
                      <span className="flex items-center gap-1"><Bot className="w-3.5 h-3.5 text-indigo-500" /> 允许本实例超级智能体调用此能力</span>
                    </label>
                  </div>
                )}
                {testState === 'fail' && (
                  <div className="mt-3 flex items-start gap-1.5 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> 测试失败：连接超时（5s），请检查后端地址与本现场网络可达性。
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between">
          <span className="text-xs text-gray-400">{form.type === 'skill' ? '上传后即可提交' : '需测试连接通过后方可提交'}</span>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">取消</button>
            <button onClick={() => { onDone && onDone(form.name || '能力'); onClose(); }} disabled={!canSubmit} className={`px-4 py-2 text-sm rounded-lg ${canSubmit ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-400'}`}>{editCap ? '保存' : '提交注册'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CapabilityCard({ cap, onEdit, onDelete, onToggle, onRetest }) {
  const [expanded, setExpanded] = useState(false);
  const meta = TYPE_META[cap.type];
  const Icon = meta.icon;
  const st = STATUS_META[cap.status];
  const iconBg = { blue: 'bg-blue-100 text-blue-600', purple: 'bg-purple-100 text-purple-600', green: 'bg-green-100 text-green-600' }[meta.color];
  const opBtn = 'p-1.5 rounded-lg hover:bg-gray-100 transition-colors';

  return (
    <div className="bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-sm transition-all">
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}><Icon className="w-4 h-4" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-900">{cap.name}</span>
                <span className="text-xs px-2 py-0.5 rounded-full border font-medium bg-gray-50 text-gray-600 border-gray-200">{meta.label}</span>
                <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{cap.category}</span>
                {cap.superAgentCallable && <span className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded border border-indigo-100"><Bot className="w-3 h-3" /> 超级智能体可调用</span>}
              </div>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">{cap.description}</p>
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                <span>{cap.version}</span>
                <span>{cap.callCount != null ? `调用 ${cap.callCount.toLocaleString()}` : `下载 ${cap.downloadCount}`}</span>
              </div>
            </div>
          </div>
          {/* 右侧：状态 + 操作（图标按钮组，常驻） */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="text-right">
              <div className="flex items-center gap-1 justify-end" title={st.desc}>
                <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} /><span className={`text-xs ${st.text}`}>{st.label}</span>
                {cap.status === 'abnormal' && <AlertTriangle className="w-3 h-3 text-red-400" />}
              </div>
              {cap.status === 'abnormal' && cap.errMsg && <div className="text-xs text-red-400 mt-0.5">{cap.errMsg}</div>}
            </div>
            <div className="flex items-center gap-0.5">
              <button onClick={() => onEdit(cap)} title="编辑" className={`${opBtn} text-gray-400 hover:text-blue-600`}><Pencil className="w-4 h-4" /></button>
              {cap.status === 'available' && <button onClick={() => onToggle(cap)} title="停用" className={`${opBtn} text-gray-400 hover:text-gray-700`}><Power className="w-4 h-4" /></button>}
              {cap.status === 'disabled' && <button onClick={() => onToggle(cap)} title="启用" className={`${opBtn} text-gray-400 hover:text-green-600`}><Power className="w-4 h-4" /></button>}
              {cap.status === 'abnormal' && <button onClick={() => onRetest(cap)} title="重新测试" className={`${opBtn} text-gray-400 hover:text-blue-600`}><Zap className="w-4 h-4" /></button>}
              {cap.type === 'skill' && <button title="下载 Skill 包" className={`${opBtn} text-gray-400 hover:text-green-600`}><Download className="w-4 h-4" /></button>}
              <button onClick={() => onDelete(cap)} title="删除" className={`${opBtn} text-gray-400 hover:text-red-500`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <button onClick={() => setExpanded(!expanded)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400" title="详情">{expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 p-4 bg-gray-50 rounded-b-xl space-y-3">
          {cap.proxyEndpoint && (
            <div className="flex items-center gap-2">
              <Shield className="w-3 h-3 text-blue-400 flex-shrink-0" />
              <code className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-mono truncate max-w-md">{cap.proxyEndpoint}</code>
              <CopyBtn text={cap.proxyEndpoint} />
            </div>
          )}
          {cap.type === 'mcp' && <div className="text-xs text-gray-400">工具名 <code className="text-purple-600">{cap.toolName}</code>；在顶部复制本实例 MCP Server 地址后自动发现此工具</div>}
          {cap.params && (
            <div>
              <div className="text-xs font-medium text-gray-600 mb-1">请求参数（注册时表格录入，自动生成 Schema / FC Schema）</div>
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <div className="grid grid-cols-[1.2fr_0.8fr_0.5fr_1.5fr_1.2fr] gap-2 px-2 py-1.5 bg-gray-50 text-xs text-gray-500 font-medium">
                  <span>参数名</span><span>类型</span><span>必填</span><span>说明</span><span>示例值</span>
                </div>
                {renderParamRowsRO(cap.params, 0)}
              </div>
            </div>
          )}
          {cap.responseExample && <div><div className="text-xs font-medium text-gray-600 mb-1">响应示例</div><div className="p-2 bg-gray-900 rounded text-xs font-mono text-green-400">{cap.responseExample}</div></div>}
          {cap.files && <div><div className="text-xs font-medium text-gray-600 mb-1">包含文件</div><div className="flex flex-wrap gap-2">{cap.files.map(f => <span key={f} className="flex items-center gap-1 text-xs bg-white border border-gray-200 text-gray-600 px-2 py-1 rounded"><BookOpen className="w-3 h-3" /> {f}</span>)}</div></div>}
        </div>
      )}
    </div>
  );
}

function GatewayBanner() {
  const mcpAddr = 'https://本实例.internal/mcp';
  return (
    <div className="grid grid-cols-2 gap-4 mb-5">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-1"><Shield className="w-4 h-4 text-blue-600" /><span className="text-sm font-semibold text-blue-800">本实例接口网关 · 强制代理</span></div>
        <p className="text-xs text-blue-600 leading-relaxed">本现场后端网络隔离，调用方无法直达。所有调用经本实例网关鉴权转发，可监控可溯源；调用量直供本实例运营看板。</p>
      </div>
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
        <div className="flex items-center justify-between gap-2 mb-1"><div className="flex items-center gap-2"><Package className="w-4 h-4 text-purple-600" /><span className="text-sm font-semibold text-purple-800">本实例 MCP Server 地址</span></div><CopyBtn text={mcpAddr} /></div>
        <code className="text-xs font-mono text-purple-700 bg-purple-100 px-2 py-1 rounded block">{mcpAddr}</code>
        <p className="text-xs text-purple-500 mt-1.5">MCP Server 程序随软件包部署在本实例；本实例 Dify/Coze 配此地址即自动发现本实例已注册的 MCP Tool（各实例独立，互不相通）。</p>
      </div>
    </div>
  );
}

export default function CapabilityRegistry() {
  const [searchText, setSearchText] = useState('');
  const [activeType, setActiveType] = useState('all');
  const [activeCategory, setActiveCategory] = useState('全部');
  const [registerOpen, setRegisterOpen] = useState(false);
  const [editCap, setEditCap] = useState(null);
  const [caps, setCaps] = useState(CAPABILITIES);
  const [toast, setToast] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2200); };
  const openRegister = () => { setEditCap(null); setRegisterOpen(true); };
  const handleEdit = (cap) => { setEditCap(cap); setRegisterOpen(true); };
  const handleDelete = (cap) => { if (window.confirm(`确定删除能力「${cap.name}」？删除后本实例将无法再调用此能力。`)) { setCaps(prev => prev.filter(c => c.id !== cap.id)); showToast(`已删除「${cap.name}」`); } };
  const handleToggle = (cap) => { const next = cap.status === 'available' ? 'disabled' : 'available'; setCaps(prev => prev.map(c => c.id === cap.id ? { ...c, status: next } : c)); showToast(`「${cap.name}」已${next === 'available' ? '启用' : '停用'}`); };
  const handleRetest = (cap) => { setCaps(prev => prev.map(c => c.id === cap.id ? { ...c, status: 'available', errMsg: undefined } : c)); showToast(`「${cap.name}」重新测试通过，已恢复可用`); };

  const filtered = caps.filter(c => {
    const mt = activeType === 'all' || c.type === activeType;
    const mc = activeCategory === '全部' || c.category === activeCategory;
    const ms = !searchText || c.name.includes(searchText) || c.description.includes(searchText);
    return mt && mc && ms;
  });
  const counts = { all: caps.length, api: caps.filter(c => c.type === 'api').length, mcp: caps.filter(c => c.type === 'mcp').length, skill: caps.filter(c => c.type === 'skill').length };

  return (
    <div className="max-w-5xl mx-auto px-6 py-6">
      <RegisterDrawer open={registerOpen} editCap={editCap} onClose={() => { setRegisterOpen(false); setEditCap(null); }} onDone={(name) => showToast(editCap ? `已保存「${name}」` : `已提交注册「${name}」`)} />
      {toast && <div className="fixed top-4 right-4 z-[60] bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" />{toast}</div>}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-bold text-gray-900">能力注册中心</h2>
          <button onClick={openRegister} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"><Plus className="w-4 h-4" /> 注册新能力</button>
        </div>
        <p className="text-sm text-gray-500">本实例能力目录 + 接口网关 + MCP Server（各项目独立部署）。产品团队随软件包<strong>预置标准能力规范</strong>，本项目管理员按现场可达后端在本地<strong>注册/测试/上架</strong>；调用经本实例网关、数据本地留存。</p>
      </div>

      <GatewayBanner />

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="搜索能力名称或描述..." value={searchText} onChange={e => setSearchText(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {[['all', `全部(${counts.all})`], ['api', `API(${counts.api})`], ['mcp', `MCP(${counts.mcp})`], ['skill', `Skill(${counts.skill})`]].map(([k, l]) => (
            <button key={k} onClick={() => setActiveType(k)} className={`px-3 py-1.5 text-xs font-medium rounded-md ${activeType === k ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <Tag className="w-3.5 h-3.5 text-gray-400" />
        {CATEGORIES.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-3 py-1 text-xs rounded-full border ${activeCategory === cat ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-600'}`}>{cat}</button>)}
      </div>

      <div className="space-y-3">
        {filtered.length === 0 ? <div className="text-center py-16 text-gray-400"><Search className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">未找到匹配的能力</p></div> : filtered.map(c => <CapabilityCard key={c.id} cap={c} onEdit={handleEdit} onDelete={handleDelete} onToggle={handleToggle} onRetest={handleRetest} />)}
      </div>

      <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-xl">
        <div className="text-xs font-medium text-gray-600 mb-2">运行状态（注册时测试连接通过即"可用"；后台轮询维持，异常自动标注并在调用时跳过）</div>
        <div className="flex flex-wrap gap-4 text-xs text-gray-500">
          {Object.values(STATUS_META).map(s => <span key={s.label} className="flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} /><span className={s.text}>{s.label}</span><span className="text-gray-400">— {s.desc}</span></span>)}
        </div>
      </div>
      <PageAiGuide pageName="能力注册中心" hints={CAP_HINTS} />
    </div>
  );
}
